(() => {
  const selectors = {
    form: '[data-product-form][data-preorder-deposit-enabled="true"]',
    config: '[data-preorder-deposit-config]',
    button: '[data-add-to-cart]',
    buttonText: '[data-add-to-cart-text]',
    modal: '[data-preorder-deposit-modal]',
    modalDialog: '.preorder-deposit-modal__dialog',
    modalProduct: '[data-preorder-modal-product]',
    modalVariantSummary: '[data-preorder-modal-variant-summary]',
    modalModel: '[data-preorder-modal-model]',
    modalModelRow: '[data-preorder-modal-model-row]',
    modalColor: '[data-preorder-modal-color]',
    modalColorRow: '[data-preorder-modal-color-row]',
    modalDeposit: '[data-preorder-modal-deposit]',
    modalBalance: '[data-preorder-modal-balance]',
    modalShipDate: '[data-preorder-modal-ship-date]',
    modalTerms: '[data-preorder-modal-terms]',
    modalCheckbox: '[data-preorder-modal-checkbox]',
    modalConfirm: '[data-preorder-modal-confirm]',
    modalError: '[data-preorder-modal-error]',
    modalClose: '[data-preorder-modal-close]',
    variantSelect: '[data-variant-select]',
    optionInput: '[name^="options["]',
    quantityInput: '[name="quantity"]'
  };

  const classes = {
    active: 'is-active',
    hidden: 'hidden',
    loading: 'loading'
  };

  const messages = {
    missingConfig: 'Pre-order is not available for this selection. Please contact us for help.',
    duplicate: 'This pre-order deposit is already in your cart.',
    fullVariantInCart: 'Remove the full-price item from cart before adding the pre-order deposit.',
    cartFetchFailed: 'We could not check your cart. Please refresh and try again.',
    cartAddFailed: 'We could not add the pre-order deposit. Please try again.',
    unavailable: 'This selection is not available for pre-order.',
    preOrder: 'Pre-order'
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const getRoutes = () => {
    const routes = window.theme && window.theme.routes && window.theme.routes.cart;
    return {
      base: routes && routes.base ? `${routes.base}.js` : '/cart.js',
      add: routes && routes.add ? `${routes.add}.js` : '/cart/add.js',
      cart: routes && routes.base ? routes.base : '/cart'
    };
  };

  const formatMoney = (cents, currency) => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value / 100);
    } catch (_error) {
      return `$${(value / 100).toFixed(2)}`;
    }
  };

  const emitAnalytics = (eventName, detail = {}) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...detail });
    }
  };

  const getSelectedVariantId = (form) => {
    const select = form.querySelector(selectors.variantSelect);
    if (select && select.value) return select.value;

    const idInput = form.querySelector('[name="id"]');
    return idInput && idInput.value ? idInput.value : form.dataset.currentProductId;
  };

  const getVariantConfig = (form) => {
    const configEl = form.querySelector(selectors.config);
    if (!configEl) return null;

    try {
      const config = JSON.parse(configEl.textContent);
      const variantId = getSelectedVariantId(form);
      return config.variants && config.variants[variantId] ? config.variants[variantId] : null;
    } catch (_error) {
      return null;
    }
  };

  const isValidVariantConfig = (variantConfig) => {
    return Boolean(
      variantConfig &&
        variantConfig.depositVariantId &&
        variantConfig.parentVariantId &&
        Number.isFinite(Number(variantConfig.fullPriceCents)) &&
        Number.isFinite(Number(variantConfig.depositAmountCents)) &&
        Number.isFinite(Number(variantConfig.balanceAmountCents)) &&
        variantConfig.currency &&
        variantConfig.estimatedShipDate &&
        variantConfig.terms
    );
  };

  const setError = (form, message) => {
    const modal = form.querySelector(selectors.modal);
    const modalError = modal && modal.querySelector(selectors.modalError);
    const inlineError = document.querySelector('[data-quantity-error]');

    if (modalError) {
      modalError.textContent = message;
      modalError.classList.remove(classes.hidden);
    }

    if (inlineError) {
      inlineError.textContent = message;
      inlineError.classList.remove(classes.hidden);
    }
  };

  const clearError = (form) => {
    const modal = form.querySelector(selectors.modal);
    const modalError = modal && modal.querySelector(selectors.modalError);
    const inlineError = document.querySelector('[data-quantity-error]');

    if (modalError) {
      modalError.textContent = '';
      modalError.classList.add(classes.hidden);
    }

    if (inlineError) {
      inlineError.classList.add(classes.hidden);
    }
  };

  const syncButton = (form) => {
    const variantConfig = getVariantConfig(form);
    const button = form.querySelector(selectors.button);
    const buttonText = button && button.querySelector(selectors.buttonText);

    if (!button) return;

    button.disabled = false;
    button.setAttribute('aria-disabled', 'false');
    button.setAttribute('aria-label', messages.preOrder);

    if (buttonText) {
      buttonText.textContent = messages.preOrder;
    }

    if (variantConfig && variantConfig.available === false && !variantConfig.depositVariantId) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', messages.unavailable);
      if (buttonText) buttonText.textContent = messages.unavailable;
    }
  };

  const scheduleButtonSync = (form) => {
    window.setTimeout(() => syncButton(form), 0);
    window.setTimeout(() => syncButton(form), 50);
  };

  const renderModal = (form, variantConfig) => {
    const modal = form.querySelector(selectors.modal);
    if (!modal) return;

    const product = modal.querySelector(selectors.modalProduct);
    const variantSummary = modal.querySelector(selectors.modalVariantSummary);
    const model = modal.querySelector(selectors.modalModel);
    const modelRow = modal.querySelector(selectors.modalModelRow);
    const color = modal.querySelector(selectors.modalColor);
    const colorRow = modal.querySelector(selectors.modalColorRow);
    const deposit = modal.querySelector(selectors.modalDeposit);
    const balance = modal.querySelector(selectors.modalBalance);
    const shipDate = modal.querySelector(selectors.modalShipDate);
    const terms = modal.querySelector(selectors.modalTerms);
    const checkbox = modal.querySelector(selectors.modalCheckbox);
    const confirm = modal.querySelector(selectors.modalConfirm);

    const productTitle = (variantConfig.productTitle || '').split(' - ')[0] || variantConfig.productTitle;
    const variantParts =
      variantConfig.variantTitle && variantConfig.variantTitle !== 'Default Title'
        ? variantConfig.variantTitle.split(' / ').map((part) => part.trim()).filter(Boolean)
        : [];

    if (product) {
      product.textContent = productTitle;
    }

    if (variantSummary) {
      variantSummary.hidden = !variantParts.length;
    }

    if (model && modelRow) {
      model.textContent = variantParts[0] || '';
      modelRow.hidden = !variantParts[0];
    }

    if (color && colorRow) {
      color.textContent = variantParts[1] || '';
      colorRow.hidden = !variantParts[1];
    }
    if (deposit) deposit.textContent = formatMoney(variantConfig.depositAmountCents, variantConfig.currency);
    if (balance) balance.textContent = formatMoney(variantConfig.balanceAmountCents, variantConfig.currency);
    if (shipDate) shipDate.textContent = variantConfig.estimatedShipDate;
    if (terms) terms.textContent = variantConfig.terms;
    if (checkbox) checkbox.checked = false;
    if (confirm) confirm.disabled = true;
  };

  const trapFocus = (modal, event) => {
    if (event.key !== 'Tab') return;

    const focusable = Array.from(modal.querySelectorAll(focusableSelector)).filter(
      (element) => element.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const closeModal = (form) => {
    const modal = form.querySelector(selectors.modal);
    if (!modal) return;

    modal.classList.remove(classes.active);
    modal.setAttribute('aria-hidden', 'true');
    document.body.removeAttribute('data-preorder-modal-open');

    if (form.preorderTrigger) {
      form.preorderTrigger.focus();
    }
  };

  const openModal = (form, variantConfig) => {
    const modal = form.querySelector(selectors.modal);
    const dialog = modal && modal.querySelector(selectors.modalDialog);
    if (!modal || !dialog) return;

    clearError(form);
    renderModal(form, variantConfig);
    form.preorderVariantConfig = variantConfig;
    form.preorderTrigger = form.querySelector(selectors.button);

    modal.classList.add(classes.active);
    modal.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('data-preorder-modal-open', 'true');
    dialog.focus();
    emitAnalytics('preorder_modal_open', { parentVariantId: variantConfig.parentVariantId });
  };

  const fetchCart = () => {
    const routes = getRoutes();
    return fetch(routes.base, {
      method: 'GET',
      credentials: 'include'
    }).then((response) => {
      if (!response.ok) throw new Error(messages.cartFetchFailed);
      return response.json();
    });
  };

  const validateCart = (cart, variantConfig) => {
    const parentVariantId = String(variantConfig.parentVariantId);
    const duplicate = cart.items.find((item) => {
      const properties = item.properties || {};
      return String(properties._preorder_parent_variant_id || '') === parentVariantId;
    });

    if (duplicate) {
      return { valid: false, message: messages.duplicate, redirectToCart: true };
    }

    const fullVariant = cart.items.find((item) => String(item.variant_id) === parentVariantId);
    if (fullVariant) {
      return { valid: false, message: messages.fullVariantInCart, redirectToCart: true };
    }

    return { valid: true };
  };

  const addDepositToCart = (variantConfig) => {
    const routes = getRoutes();
    const deposit = formatMoney(variantConfig.depositAmountCents, variantConfig.currency);
    const balance = formatMoney(variantConfig.balanceAmountCents, variantConfig.currency);
    const itemLabel =
      variantConfig.variantTitle && variantConfig.variantTitle !== 'Default Title'
        ? `Deposit for ${variantConfig.productTitle} - ${variantConfig.variantTitle}`
        : `Deposit for ${variantConfig.productTitle}`;

    const payload = {
      items: [
        {
          id: Number(variantConfig.depositVariantId),
          quantity: 1,
          properties: {
            _preorder_parent_product_id: String(variantConfig.parentProductId),
            _preorder_parent_variant_id: String(variantConfig.parentVariantId),
            _preorder_product_title: variantConfig.productTitle,
            _preorder_selected_variant_title: variantConfig.variantTitle,
            _preorder_full_price_cents: String(variantConfig.fullPriceCents),
            _preorder_deposit_amount_cents: String(variantConfig.depositAmountCents),
            _preorder_balance_amount_cents: String(variantConfig.balanceAmountCents),
            _preorder_currency: variantConfig.currency,
            _preorder_estimated_ship_date: variantConfig.estimatedShipDate,
            _preorder_terms_accepted: 'true',
            _preorder_terms_accepted_at: new Date().toISOString(),
            'Pre-order item': itemLabel,
            'Balance due before shipment': balance,
            'Estimated ship date': variantConfig.estimatedShipDate,
            'Deposit terms': `Non-refundable deposit paid today: ${deposit}`
          }
        }
      ]
    };

    return fetch(routes.add, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    }).then((response) => {
      if (!response.ok) throw new Error(messages.cartAddFailed);
      return response.json();
    });
  };

  const openCartSurface = () => {
    const quickCart = document.querySelector('.quick-cart__wrapper');

    document.dispatchEvent(new Event('apps:product-added-to-cart'));

    if (quickCart) {
      quickCart.classList.add('active');
      document.body.setAttribute('data-fluorescent-overlay-open', 'true');
      return;
    }

    window.location.href = getRoutes().cart;
  };

  const setLoading = (form, loading) => {
    const buttons = form.querySelectorAll(selectors.button);
    buttons.forEach((button) => {
      button.classList.toggle(classes.loading, loading);
      button.disabled = loading;
      button.setAttribute('aria-disabled', loading ? 'true' : 'false');
    });
  };

  const submitDeposit = (form) => {
    const variantConfig = form.preorderVariantConfig || getVariantConfig(form);

    if (!isValidVariantConfig(variantConfig)) {
      setError(form, messages.missingConfig);
      emitAnalytics('preorder_deposit_add_failed', { reason: 'missing_config' });
      return;
    }

    clearError(form);
    setLoading(form, true);

    fetchCart()
      .then((cart) => {
        const validation = validateCart(cart, variantConfig);
        if (!validation.valid) {
          const error = new Error(validation.message);
          error.redirectToCart = validation.redirectToCart;
          throw error;
        }

        return addDepositToCart(variantConfig);
      })
      .then(() => {
        emitAnalytics('preorder_deposit_add_success', { parentVariantId: variantConfig.parentVariantId });
        closeModal(form);
        openCartSurface();
      })
      .catch((error) => {
        const message = error && error.message ? error.message : messages.cartAddFailed;
        setError(form, message);
        emitAnalytics('preorder_deposit_add_failed', {
          parentVariantId: variantConfig.parentVariantId,
          reason: message
        });

        if (error && error.redirectToCart) {
          window.setTimeout(() => {
            window.location.href = getRoutes().cart;
          }, 900);
        }
      })
      .finally(() => {
        setLoading(form, false);
        syncButton(form);
      });
  };

  const bindModal = (form) => {
    const modal = form.querySelector(selectors.modal);
    if (!modal) return;

    const checkbox = modal.querySelector(selectors.modalCheckbox);
    const confirm = modal.querySelector(selectors.modalConfirm);

    modal.querySelectorAll(selectors.modalClose).forEach((closeButton) => {
      closeButton.addEventListener('click', () => closeModal(form));
    });

    if (checkbox && confirm) {
      checkbox.addEventListener('change', () => {
        confirm.disabled = !checkbox.checked;
        if (checkbox.checked) {
          emitAnalytics('preorder_terms_accept', {
            parentVariantId: form.preorderVariantConfig && form.preorderVariantConfig.parentVariantId
          });
        }
      });
    }

    if (confirm) {
      confirm.addEventListener('click', () => submitDeposit(form));
    }

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModal(form);
      } else {
        trapFocus(modal, event);
      }
    });
  };

  const bindVariantSync = (form) => {
    const sync = () => scheduleButtonSync(form);

    form.addEventListener('change', sync);
    form.querySelectorAll(selectors.optionInput).forEach((input) => {
      input.addEventListener('change', sync);
    });

    window.addEventListener('product:variant-change', sync);
  };

  const initForm = (form) => {
    if (form.dataset.preorderDepositInitialized === 'true') return;

    form.dataset.preorderDepositInitialized = 'true';
    bindModal(form);
    bindVariantSync(form);
    scheduleButtonSync(form);
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest(selectors.form);
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const variantConfig = getVariantConfig(form);
      if (!isValidVariantConfig(variantConfig)) {
        setError(form, messages.missingConfig);
        emitAnalytics('preorder_deposit_add_failed', { reason: 'missing_config' });
        return;
      }

      openModal(form, variantConfig);
    },
    true
  );

  const init = () => {
    document.querySelectorAll(selectors.form).forEach(initForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
